import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    FunctionField,
    ShowButton,
    Filter,
    TextInput,
    SelectInput,
    TopToolbar,
    CreateButton,
    RefreshButton,
    useTranslate,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Chip, Box, Typography, Tooltip, Fab, useMediaQuery, useTheme } from '@mui/material';
import {
    Schedule,
    CloudDownload,
    CloudDone,
    Error as ErrorIcon,
    Cancel,
    Pending,
    CloudUpload,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const FirmwareUpdatesFilter = (props: any) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.firmware-updates.list.filters.search')}
                source="q"
                alwaysOn
            />
            <SelectInput
                source="status"
                label={translate('resources.firmware-updates.list.filters.status')}
                choices={[
                    { id: 'pending', name: translate('resources.firmware-updates.statuses.pending') },
                    { id: 'downloading', name: translate('resources.firmware-updates.statuses.downloading') },
                    { id: 'downloaded', name: translate('resources.firmware-updates.statuses.downloaded') },
                    { id: 'installing', name: translate('resources.firmware-updates.statuses.installing') },
                    { id: 'installed', name: translate('resources.firmware-updates.statuses.installed') },
                    { id: 'installation_failed', name: translate('resources.firmware-updates.statuses.installation_failed') },
                    { id: 'download_failed', name: translate('resources.firmware-updates.statuses.download_failed') },
                    { id: 'cancelled', name: translate('resources.firmware-updates.statuses.cancelled') },
                ]}
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <RefreshButton />
    </Box>
);

const StatusIcon = ({ status }: { status: string }) => {
    switch (status) {
        case 'pending':
            return <Pending fontSize="small" color="action" />;
        case 'downloading':
            return <CloudDownload fontSize="small" color="primary" />;
        case 'downloaded':
            return <CloudDone fontSize="small" color="success" />;
        case 'installing':
            return <CloudUpload fontSize="small" color="primary" />;
        case 'installed':
            return <CloudDone fontSize="small" color="success" />;
        case 'installation_failed':
        case 'download_failed':
            return <ErrorIcon fontSize="small" color="error" />;
        case 'cancelled':
            return <Cancel fontSize="small" color="action" />;
        default:
            return <Pending fontSize="small" />;
    }
};

const StatusChip = ({ status }: { status: string }) => {
    const translate = useTranslate();
    let color: 'default' | 'primary' | 'success' | 'error' | 'warning' = 'default';

    switch (status) {
        case 'pending':
            color = 'default';
            break;
        case 'in_progress':
        case 'downloading':
        case 'installing':
            color = 'primary';
            break;
        case 'completed':
        case 'downloaded':
        case 'installed':
            color = 'success';
            break;
        case 'failed':
        case 'installation_failed':
        case 'download_failed':
            color = 'error';
            break;
        case 'cancelled':
            color = 'warning';
            break;
    }

    return (
        <Chip
            label={translate(`resources.firmware-updates.statuses.${status}`)}
            color={color}
            size="small"
            icon={<StatusIcon status={status} />}
        />
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box
                        onClick={() => navigate(`/firmware-updates/${record.id}/show`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {record.site_names && record.site_names.length > 0 ? record.site_names.join(', ') : '-'}
                            </Typography>
                            <Box sx={{ flexShrink: 0 }}>
                                <StatusChip status={record.status || 'pending'} />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {record.firmware?.vendor || '-'} {record.firmware?.model || '-'}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {record.firmware?.version ? `v${record.firmware.version}` : '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {record.total_chargers || 0} chargers
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

const FirmwareUpdatesList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={<FirmwareUpdatesFilter />}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                sort={{ field: 'created_at', order: 'DESC' }}
                perPage={25}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': {
                            margin: 0,
                        },
                        '& .RaList-content': {
                            backgroundColor: 'transparent !important',
                            boxShadow: 'none !important',
                            mt: 2,
                        },
                        '& .RaList-actions': {
                            backgroundColor: 'transparent !important',
                            p: '0 !important',
                            minHeight: 'auto !important',
                        },
                        '& .RaListToolbar-root': {
                            backgroundColor: 'transparent !important',
                            flexDirection: 'row !important',
                            flexWrap: 'wrap !important',
                            alignItems: 'center !important',
                            gap: '8px !important',
                        },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': {
                            flex: '1 1 100% !important',
                            width: '100% !important',
                        },
                        '& .filter-field, & .RaFilterForm-filterFormInput': {
                            width: '100% !important',
                        },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': {
                            backgroundColor: 'transparent !important',
                            minHeight: 'auto !important',
                        },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        rowClick="show"
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                            '& .column-site': { width: '150px' },
                            '& .column-vendor': { width: '120px' },
                            '& .column-model': { width: '120px' },
                            '& .column-version': { width: '100px' },
                            '& .column-total_chargers': { width: '120px' },
                            '& .column-status': { width: '150px' },
                            '& .column-created_at': { width: '180px' },
                            '& .column-actions': { width: '80px' },
                        }}
                    >
                        <FunctionField
                            label={translate('resources.firmware-updates.fields.site')}
                            render={(record: any) => {
                                if (record.site_names && record.site_names.length > 0) {
                                    return record.site_names.join(', ');
                                }
                                return '-';
                            }}
                            source="site"
                        />
                        <FunctionField
                            label={translate('resources.firmware-updates.fields.vendor')}
                            render={(record: any) => record.firmware?.vendor || '-'}
                            source="vendor"
                        />
                        <FunctionField
                            label={translate('resources.firmware-updates.fields.model')}
                            render={(record: any) => record.firmware?.model || '-'}
                            source="model"
                        />
                        <FunctionField
                            label={translate('resources.firmware-updates.fields.version')}
                            render={(record: any) => record.firmware?.version ? `v${record.firmware.version}` : '-'}
                            source="version"
                            sx={{ fontWeight: 600 }}
                        />
                        <TextField
                            source="total_chargers"
                            label={translate('resources.firmware-updates.fields.total_chargers')}
                        />
                        <FunctionField
                            label={translate('resources.firmware-updates.fields.status')}
                            render={(record: any) => <StatusChip status={record.status || 'pending'} />}
                            source="status"
                        />
                        <DateField
                            source="created_at"
                            label={translate('resources.firmware-updates.fields.created_at')}
                            locales="en-GB"
                            options={{
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }}
                        />
                        <FunctionField
                            label=""
                            render={(record: any) => (
                                <Box display="flex" gap={0}>
                                    <Tooltip title={translate('ra.action.show')}>
                                        <span>
                                            <ShowButton record={record} size="small" />
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                            source="actions"
                            textAlign="right"
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/firmware-updates/create')}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        backgroundColor: '#009A75',
                        '&:hover': { backgroundColor: '#007A5E' },
                        zIndex: 1100,
                    }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};

export default FirmwareUpdatesList;
